import { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import AdsBanner, { BannerPosition } from "@components/ads-banner/ads-banner";
import ChallengeComplete from "@components/challenge-complete/challenge-complete";
import QueryHandler from "@components/query-handler/query-handler";
import ShareIcons from "@components/share/share-icons";
import WrapperAdsBanner from "@components/wrapper-ads-banner/WrapperAdsBanner";
import WrapperRewardsModals from "@components/wrapper-rewards-modals/wrapper-rewards-modals";

import { useAppDispatch } from "@hooks/use-store";
import { formatUrlWithLocale } from "@helpers/format-url-with-locale";
import {
  getOrdinalSuffix,
  getStoredRewardIds,
  rewardsModalContent,
  updateStoredRewardIds,
} from "@helpers/helpers";
import { ExerciseCompleteData, IReward, Locales } from "@typings/global.types";

import { useGetExerciseEntrypointQuery } from "../../api/exercise-entrypoint";
import { exercisesApi } from "../../api/exercises";
import { useUpdateUserInfoMutation } from "../../api/user-info";

type streakAnimationType =
  | "appearAndBounce"
  | "goDownAndShake"
  | "allScale"
  | "allBounce"
  | "allRolling";

const animationSettings: Record<
  streakAnimationType,
  {
    confettiDuration: number;
    confettiDelay: number;
    delays: {
      circles: number;
      sticks: number;
      curls: number;
    };
  }
> = {
  appearAndBounce: {
    confettiDuration: 1,
    confettiDelay: 0.5,
    delays: { circles: 0, sticks: 0.6, curls: 0.8 },
  },
  goDownAndShake: {
    confettiDuration: 1,
    confettiDelay: 1.8,
    delays: { circles: 0, sticks: 0.1, curls: 0.2 },
  },
  allScale: {
    confettiDuration: 1.6,
    confettiDelay: 0,
    delays: { circles: 0, sticks: 0, curls: 0 },
  },
  allBounce: {
    confettiDuration: 1.2,
    confettiDelay: 0.8,
    delays: { circles: 0, sticks: 0, curls: 0 },
  },
  allRolling: {
    confettiDuration: 1.2,
    confettiDelay: 0.8,
    delays: { circles: 0, sticks: 0, curls: 0 },
  },
};

// Функція для вибору випадкової анімації
function getRandomAnimation(): streakAnimationType {
  const animations: streakAnimationType[] = [
    "appearAndBounce",
    "goDownAndShake",
    "allScale",
    "allBounce",
    "allRolling",
  ];
  const randomIndex = Math.floor(Math.random() * animations.length);
  return animations[randomIndex];
}

const getRandomTranslationId = () => {
  const translationIds = [
    "nice-work",
    "well-done",
    "good-job",
    "good-work",
    "very-good",
    "high-five",
    "up-top",
    "great-work",
    "nailed-it",
    "bravo",
    "good-score",
  ];
  const randomIndex = Math.floor(Math.random() * translationIds.length);
  return translationIds[randomIndex];
};

function WrapperChallengeComplete() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const btn = useRef<HTMLButtonElement>(null);

  const { data, isLoading } = useGetExerciseEntrypointQuery({
    exerciseId: 0,
  });

  const randomId = getRandomTranslationId();
  const dayOfChallenge =
    data?.user.locale === "en_US"
      ? `${data.user.strixExercise}${getOrdinalSuffix(data.user.strixExercise)}`
      : data?.user.strixExercise || 1;

  const [selectedAnimation, setSelectedAnimation] =
    useState<streakAnimationType>(getRandomAnimation());

  const [exerciseCompleteData, setExerciseCompleteData] =
    useState<ExerciseCompleteData | null>(null);

  const [currentModalType, setCurrentModalType] = useState<number | null>(null);

  const onClickNext = () => {
    if (exerciseCompleteData) {
      if (exerciseCompleteData.lastLesson) {
        // закінчився урок
        navigate(formatUrlWithLocale("/typing-tutor/lesson-complete/"));
      }
      if (exerciseCompleteData.lastCourse) {
        // закінчився курс
        navigate(formatUrlWithLocale("/typing-tutor/course-complete/"));
      }
      // закінчилася вправа
      if (
        !exerciseCompleteData.lastLesson &&
        !exerciseCompleteData.lastCourse
      ) {
        window.location.href = formatUrlWithLocale("/typing-tutor/");
      }
    }
  };

  useEffect(() => {
    const sessionStorageData = window.sessionStorage.getItem(
      "exerciseCompleteData",
    );
    if (sessionStorageData) {
      setExerciseCompleteData(JSON.parse(sessionStorageData));
    } else {
      navigate(formatUrlWithLocale("/typing-tutor/"));
    }
  }, [dispatch, navigate]);

  const keyPress = (event: any): void => {
    if (event.keyCode === 32 || event.keyCode === 13) {
      onClickNext();
    }
  };

  useEffect(() => {
    if (btn.current) {
      btn.current.focus();
    }
  }, [btn, isLoading]);

  useEffect(() => {
    if (exerciseCompleteData && data) {
      const storedRewardIds = getStoredRewardIds(data.user.id);
      const currentRewardIds = exerciseCompleteData.rewards.map(
        (reward: IReward) => reward.id,
      );

      if (!storedRewardIds) {
        updateStoredRewardIds(currentRewardIds, data.user.id);
        return;
      }

      const newRewardIds = currentRewardIds.filter(
        (id) => !storedRewardIds.includes(id) && [9].includes(id),
      );

      if (newRewardIds.length > 0) {
        updateStoredRewardIds(
          [...storedRewardIds, ...newRewardIds],
          data.user.id,
        );
      }

      const validRewardIds = newRewardIds.filter(
        (id) => rewardsModalContent[id as keyof typeof rewardsModalContent],
      );

      if (validRewardIds.length > 0) {
        const timeoutId = setTimeout(() => {
          setCurrentModalType(validRewardIds[0]);
        }, 500);
        // eslint-disable-next-line consistent-return
        return () => clearTimeout(timeoutId);
      }
    }
  }, [data, exerciseCompleteData]);

  const handleCloseRewardModal = () => {
    setCurrentModalType(null);
  };

  return (
    <QueryHandler isLoading={isLoading}>
      <div className='relative mx-auto mt-9 max-w-container-xsmall'>
        <ChallengeComplete
          confettiDuration={
            animationSettings[selectedAnimation].confettiDuration
          }
          confettiDelay={animationSettings[selectedAnimation].confettiDelay}
          delays={animationSettings[selectedAnimation].delays}
          animationName={selectedAnimation}
          dayOfChallenge={data?.user?.strixExercise || 1}
        />
        <h2 className='mb-4 text-center'>
          <FormattedMessage id={randomId} />
          <br />
          <FormattedMessage
            id='day-of-your-typing-marathon'
            values={{
              day: dayOfChallenge,
            }}
          />
        </h2>
        <div className='mb-4 flex justify-center'>
          <button
            ref={btn}
            className='btn btn-primary'
            onKeyDown={keyPress}
            onClick={onClickNext}
            type='button'
          >
            <FormattedMessage id='next' />
          </button>
        </div>
        <hr className='dark:border-brd-dark-primary mb-4 border-t-2 border-brd-primary' />
      </div>
      <div className='min-h-[110px]'>
        <WrapperAdsBanner page='challenge-complete'>
          <AdsBanner bannerPosition={BannerPosition.CHALLENGE_BOTTOM} />
        </WrapperAdsBanner>
      </div>
      {data?.user.rewards && (
        <WrapperRewardsModals
          closeModalHandler={handleCloseRewardModal}
          modalType={currentModalType}
          queueModal={0}
        />
      )}
    </QueryHandler>
  );
}

export default WrapperChallengeComplete;
